import produce from 'immer';
import { useContext, useMemo } from 'react';

import { CustomFilterMapping } from '@work4all/components';
import { pickerToQuery } from '@work4all/components/lib/dataDisplay/basic-table/utils/filters/filterConversion';

import { nameof } from '@work4all/data/lib/helper/nameof';

import { ChronoFileItem } from '@work4all/models/lib/Classes/ChronoFileItem.entity';
import { SortDirection, SortOption } from '@work4all/models/lib/DataProvider';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';
import { FileType } from '@work4all/models/lib/File';
import { ITableSchema } from '@work4all/models/lib/table-schema/table-schema';

import {
  IUseDataTableV8Options,
  useDataTableV8,
} from '../../../../../../components/data-tables/use-data-table-v8';
import { settings, useSetting } from '../../../../../../settings';
import { useSelectedContact } from '../../card-widgets/hooks/useEntityLinks';
import { FileContext } from '../../FileContext';
import { ContactCell } from '../cells/ContactCell';
import { DescriptionCell } from '../cells/DescriptionCell';
import { EmptyCell } from '../cells/EmptyCell';
import { EntityCell } from '../cells/EntityCell';
import { ProjectCell } from '../cells/ProjectCell';
import { SumCell } from '../cells/SumCell';
import { UserCell } from '../cells/UserCell';

import { useChronoFileRequestFields } from './useChronoFileRequestFields';

export interface IAggregatedList
  extends Pick<IUseDataTableV8Options<any>, 'onRowsSelected'> {
  id?: string | number;
  type?: FileType;
  layout?: 'table' | 'cards' | 'board';
  schema: ITableSchema<never>;
}

const defaultSort: SortOption[] = [
  {
    field: nameof<ChronoFileItem>('dateTime'),
    direction: SortDirection.DESCENDING,
  },
];

export const useChronoFileList = (props: IAggregatedList) => {
  const { layout, schema, id, type, onRowsSelected } = props;

  const { contactMetadata } = useContext(FileContext);
  const selectedContact = useSelectedContact(contactMetadata);

  const customFilterMapping: CustomFilterMapping = useMemo(
    () => ({
      ChronoContactPicker: (id: string, filterValue: unknown) => {
        const query = pickerToQuery(
          //eslint-disable-next-line
          //@ts-ignore
          Array.isArray(filterValue) ? filterValue : [filterValue]
        );
        return [['contactCode', query]];
      },
    }),
    []
  );

  const prefilter = useMemo(() => {
    if (type === FileType.PROJECT) {
      return {
        $or: [
          { projectId: { $eq: id.toString() } },
          { 'project.parentProject.id': { $eq: id.toString() } },
        ],
      };
    }

    const baseFilter: unknown[] = [
      { sdObjMemberCode: { $eq: id } },
      {
        sdObjType: { $eq: convertFileTypeToSdObjType(type) },
      },
    ];
    if (selectedContact)
      baseFilter.push({
        //eslint-disable-next-line
        //@ts-ignore
        contactCode: { $eq: selectedContact.id },
      });

    return baseFilter;
  }, [id, type, selectedContact]);

  const forceRequestFields = useChronoFileRequestFields();

  const schemaOverride = useMemo(() => {
    if (type !== FileType.PROJECT) return schema;

    return produce(schema, (draft) => {
      const contactColumn = draft.columns.find(
        (column) => column.id === 'Contact'
      );

      if (contactColumn) {
        contactColumn.filterable = false;
      }
    });
  }, [type, schema]);

  const config = useDataTableV8({
    layout,
    schema: schemaOverride,
    defaultSort,
    //eslint-disable-next-line
    //@ts-ignore
    prefilter,
    forceRequestFields,
    customFilterMapping,
    onRowsSelected,
    enableURLState: false,
    cells: {
      Empty: EmptyCell,
      Entity: EntityCell,
      Contact: ContactCell,
      Sum: SumCell,
      Project: ProjectCell,
      User: UserCell,
      Description: DescriptionCell,
    },
  });

  const tableSettings = useSetting(
    settings.tableConfig({ entityType: schema.entity, layout })
  );

  return {
    ...config,
    resetColumns: tableSettings.delete,
  };
};

function convertFileTypeToSdObjType(entity: FileType): SdObjType {
  switch (entity) {
    case FileType.CUSTOMER:
      return SdObjType.KUNDE;
    case FileType.SUPPLIER:
      return SdObjType.LIEFERANT;
    case FileType.PROJECT:
      return SdObjType.PROJEKT;
    default:
      throw new Error(`Cannot convert entity "${entity}" to \`SdObjType\``);
  }
}
